import { useState, useEffect } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { 
  Search, 
  Package, 
  Plus, 
  History,
  AlertCircle,
  CheckCircle,
  Loader2,
  ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSmsPacksPage() {
  const { getToken } = useTenant();
  
  // State
  const [businesses, setBusinesses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [smsPacks, setSmsPacks] = useState([]);
  const [packSummary, setPackSummary] = useState(null);
  const [usageHistory, setUsageHistory] = useState([]);
  const [usageTotal, setUsageTotal] = useState(0);
  const [usagePage, setUsagePage] = useState(1);
  
  // Loading states
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [bonusForm, setBonusForm] = useState({
    sms_quantity: '',
    source: 'admin_bonus',
    reason: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [toast, setToast] = useState(null);
  
  // Fetch businesses
  const fetchBusinesses = async (search = '') => {
    setLoadingBusinesses(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ page_size: '100' });
      if (search) params.append('search', search);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/businesses?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch businesses');
      
      const data = await response.json();
      setBusinesses(data.businesses || []);
    } catch (err) {
      console.error('Error fetching businesses:', err);
      showToast('error', 'Failed to load businesses');
    } finally {
      setLoadingBusinesses(false);
    }
  };
  
  // Fetch SMS packs for selected business
  const fetchSmsPacks = async (businessId) => {
    setLoadingPacks(true);
    try {
      const token = await getToken();
      const response = await fetch(
        `${BACKEND_URL}/api/admin/businesses/${businessId}/sms-packs?include_inactive=true`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Failed to fetch SMS packs');
      
      const data = await response.json();
      setSmsPacks(data.packs || []);
      setPackSummary(data.summary);
    } catch (err) {
      console.error('Error fetching SMS packs:', err);
      showToast('error', 'Failed to load SMS packs');
    } finally {
      setLoadingPacks(false);
    }
  };
  
  // Fetch usage history
  const fetchUsageHistory = async (businessId, page = 1) => {
    setLoadingUsage(true);
    try {
      const token = await getToken();
      const response = await fetch(
        `${BACKEND_URL}/api/admin/businesses/${businessId}/pack-usage-history?page=${page}&page_size=20`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Failed to fetch usage history');
      
      const data = await response.json();
      setUsageHistory(data.usage_history || []);
      setUsageTotal(data.total_records || 0);
      setUsagePage(page);
    } catch (err) {
      console.error('Error fetching usage history:', err);
      showToast('error', 'Failed to load usage history');
    } finally {
      setLoadingUsage(false);
    }
  };
  
  // Handle business selection
  const handleSelectBusiness = (business) => {
    setSelectedBusiness(business);
    fetchSmsPacks(business.id);
    fetchUsageHistory(business.id, 1);
    
    // Save to localStorage
    localStorage.setItem('admin_last_business', business.id);
  };
  
  // Add bonus SMS
  const handleAddBonusSms = async (e) => {
    e.preventDefault();
    
    // Validate
    const errors = {};
    if (!bonusForm.sms_quantity || bonusForm.sms_quantity <= 0) {
      errors.sms_quantity = 'Must be a positive number';
    }
    if (!bonusForm.reason || bonusForm.reason.length < 5) {
      errors.reason = 'Reason must be at least 5 characters';
    }
    if (bonusForm.reason && bonusForm.reason.length > 500) {
      errors.reason = 'Reason must be less than 500 characters';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setSubmitting(true);
    setFormErrors({});
    
    try {
      const token = await getToken();
      const response = await fetch(
        `${BACKEND_URL}/api/admin/businesses/${selectedBusiness.id}/add-bonus-sms`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(bonusForm)
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to add bonus SMS');
      }
      
      const data = await response.json();
      
      // Success - refresh data
      await fetchSmsPacks(selectedBusiness.id);
      await fetchUsageHistory(selectedBusiness.id, 1);
      
      // Reset form
      setBonusForm({ sms_quantity: '', source: 'admin_bonus', reason: '' });
      
      // Show success
      showToast('success', `Successfully added ${data.sms_quantity} bonus SMS credits`);
      
    } catch (err) {
      console.error('Error adding bonus SMS:', err);
      showToast('error', err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Toast helper
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };
  
  // Initialize
  useEffect(() => {
    fetchBusinesses();
    
    // Restore last business
    const lastBusinessId = localStorage.getItem('admin_last_business');
    if (lastBusinessId) {
      // Will be selected once businesses load
      setTimeout(() => {
        const business = businesses.find(b => b.id === lastBusinessId);
        if (business) handleSelectBusiness(business);
      }, 500);
    }
  }, []);
  
  // Search handler
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        fetchBusinesses(searchQuery);
      } else {
        fetchBusinesses();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">SMS Pack Management</h1>
        <p className="text-gray-600 mt-1">
          View and manage SMS credits for businesses
        </p>
      </div>
      
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg ${
          toast.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          {toast.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600" />
          )}
          <span className={`text-sm font-medium ${
            toast.type === 'success' ? 'text-green-800' : 'text-red-800'
          }`}>
            {toast.message}
          </span>
        </div>
      )}
      
      {/* Business Selector */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Business</h2>
        
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by business name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>

        {loadingBusinesses ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : businesses.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No businesses found</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {businesses.map((business) => (
              <button
                key={business.id}
                onClick={() => handleSelectBusiness(business)}
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  selectedBusiness?.id === business.id
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="font-medium text-gray-900">{business.name}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {new Date(business.created_at).toLocaleDateString()}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Main Content - Only show if business selected */}
      {selectedBusiness && (
        <>
          {/* Summary Stats */}
          {packSummary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="text-sm text-gray-600 mb-1">Total Purchased</div>
                <div className="text-2xl font-bold text-gray-900">
                  {packSummary.total_sms_purchased.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 mt-1">SMS</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="text-sm text-gray-600 mb-1">Remaining</div>
                <div className="text-2xl font-bold text-green-600">
                  {packSummary.total_sms_remaining.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 mt-1">SMS available</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="text-sm text-gray-600 mb-1">Consumed</div>
                <div className="text-2xl font-bold text-gray-600">
                  {packSummary.total_sms_consumed.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 mt-1">SMS used</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="text-sm text-gray-600 mb-1">Active Packs</div>
                <div className="text-2xl font-bold text-purple-600">
                  {packSummary.active_pack_count}
                </div>
                <div className="text-xs text-gray-500 mt-1">packs</div>
              </div>
            </div>
          )}
          
          {/* Add Bonus SMS Form */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Add Bonus SMS
            </h2>
            
            <form onSubmit={handleAddBonusSms} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SMS Quantity *
                  </label>
                  <input
                    type="number"
                    value={bonusForm.sms_quantity}
                    onChange={(e) => setBonusForm({ ...bonusForm, sms_quantity: parseInt(e.target.value) || '' })}
                    placeholder="1000"
                    min="1"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                      formErrors.sms_quantity ? 'border-red-300' : 'border-gray-200'
                    }`}
                  />
                  {formErrors.sms_quantity && (
                    <p className="text-sm text-red-600 mt-1">{formErrors.sms_quantity}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Source *
                  </label>
                  <select
                    value={bonusForm.source}
                    onChange={(e) => setBonusForm({ ...bonusForm, source: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="admin_bonus">Admin Bonus</option>
                    <option value="promo">Promo</option>
                    <option value="bundle_bonus">Bundle Bonus</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason * (5-500 characters)
                </label>
                <textarea
                  value={bonusForm.reason}
                  onChange={(e) => setBonusForm({ ...bonusForm, reason: e.target.value })}
                  placeholder="Explain why bonus SMS are being added..."
                  rows={3}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                    formErrors.reason ? 'border-red-300' : 'border-gray-200'
                  }`}
                />
                <div className="flex justify-between mt-1">
                  {formErrors.reason && (
                    <p className="text-sm text-red-600">{formErrors.reason}</p>
                  )}
                  <p className="text-xs text-gray-500 ml-auto">
                    {bonusForm.reason.length}/500
                  </p>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2.5 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Add Bonus SMS
                  </>
                )}
              </button>
            </form>
          </div>
          
          {/* SMS Packs List */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              SMS Packs
            </h2>
            
            {loadingPacks ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
              </div>
            ) : smsPacks.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No SMS packs found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Pack ID</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Source</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">Total SMS</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">Remaining</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">Rate (¢/SMS)</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Purchased</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {smsPacks.map((pack) => (
                      <tr key={pack.pack_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <span className="font-mono text-xs text-gray-600">
                            {pack.pack_id.slice(0, 8)}...
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                            pack.source === 'paid' ? 'bg-blue-100 text-blue-700' :
                            pack.source === 'admin_bonus' ? 'bg-green-100 text-green-700' :
                            pack.source === 'promo' ? 'bg-purple-100 text-purple-700' :
                            pack.source === 'migration' ? 'bg-gray-100 text-gray-700' :
                            'bg-orange-100 text-orange-700'
                          }`}>
                            {pack.source.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right font-medium">
                          {pack.total_sms.toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-right font-medium text-green-600">
                          {pack.remaining_sms.toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-sm">
                          {pack.cost_per_sms_cents === 0 ? (
                            <span className="text-green-600 font-medium">FREE</span>
                          ) : (
                            `${pack.cost_per_sms_cents.toFixed(3)}¢`
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {new Date(pack.purchased_at).toLocaleDateString('en-AU', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                            pack.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {pack.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {pack.stripe_payment_id ? (
                            <span className="text-xs text-gray-500 font-mono flex items-center gap-1">
                              {pack.stripe_payment_id.slice(0, 12)}...
                              <ExternalLink className="w-3 h-3" />
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* FIFO Usage History */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <History className="w-5 h-5" />
              FIFO Usage History
            </h2>
            
            {loadingUsage ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
              </div>
            ) : usageHistory.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No usage history</p>
            ) : (
              <>
                <div className="overflow-x-auto mb-4">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Campaign</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Pack ID</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">SMS Used</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">Cost (derived)</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usageHistory.map((usage) => (
                        <tr key={usage.usage_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="font-medium text-gray-900">{usage.campaign_name || 'Unknown'}</div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-mono text-xs text-gray-600">
                              {usage.pack_id.slice(0, 8)}...
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right font-medium">
                            {usage.sms_count.toLocaleString()}
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-sm text-gray-700">
                            ${(usage.cost_cents / 100).toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {new Date(usage.used_at).toLocaleString('en-AU', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {usageTotal > 20 && (
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="text-sm text-gray-600">
                      Showing {((usagePage - 1) * 20) + 1}-{Math.min(usagePage * 20, usageTotal)} of {usageTotal}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => fetchUsageHistory(selectedBusiness.id, usagePage - 1)}
                        disabled={usagePage === 1}
                        className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => fetchUsageHistory(selectedBusiness.id, usagePage + 1)}
                        disabled={usagePage * 20 >= usageTotal}
                        className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
